import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import BrandLogo from '../BrandLogo';
import { 
  TrendingUp, 
  Globe, 
  ChevronDown, 
  Menu, 
  X,
  User,
  LogOut
} from 'lucide-react';

const Header = ({ 
  user, 
  language, 
  activeSection, 
  onLogin, 
  onRegister, 
  onLogout, 
  onSwitchLanguage, 
  onNavigate 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { tt } = useLanguage();
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  // PWA install button is shown ONLY inside TradingApp (for authenticated clients),
  // not on landing — see /app/frontend/public/index.html and /app/frontend/src/components/trading/TradingApp.js

  const translations = {
    ru: {
      home: 'Главная',
      about: 'О компании',
      features: 'Возможности',
      assets: 'Активы',
      education: 'Обучение',
      login: 'Вход',
      register: 'Регистрация',
      platform: 'Торговая платформа',
      logout: 'Выход',
      profile: 'Профиль',
      installApp: 'Скачать приложение'
    },
    en: {
      home: 'Home',
      about: 'About',
      features: 'Features',
      assets: 'Assets',
      education: 'Education',
      login: 'Login',
      register: 'Register',
      platform: 'Trading Platform',
      logout: 'Logout',
      profile: 'Profile',
      installApp: 'Download App'
    },
    kk: {
      home: 'Басты бет',
      about: 'Компания туралы',
      features: 'Мүмкіндіктер',
      assets: 'Активтер',
      education: 'Оқыту',
      login: 'Кіру',
      register: 'Тіркелу',
      platform: 'Сауда платформасы',
      logout: 'Шығу',
      profile: 'Профиль'
    }
  };

  const t = translations[language];

  const languages = [
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'kk', name: 'Қазақша', flag: '🇰🇿' }
  ];

  const navigationItems = [
    { id: 'home', label: t.home },
    { id: 'about', label: t.about },
    { id: 'features', label: t.features },
    { id: 'assets', label: t.assets },
    { id: 'education', label: t.education }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="cursor-pointer" onClick={() => onNavigate('home')}>
            <BrandLogo size="md" />
          </div>

          {/* Desktop Navigation */}
          <nav className="md:flex items-center space-x-8 hidden md:!flex">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                  activeSection === item.id ? 'text-emerald-600' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Side - Desktop only auth buttons */}
          <div className="md:flex items-center space-x-4 hidden md:!flex">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-emerald-600 transition-colors border border-gray-200 rounded-lg hover:border-emerald-300"
              >
                <Globe className="w-4 h-4" />
                <span>{languages.find(l => l.code === language)?.flag}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {isLangDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsLangDropdownOpen(false)} />
                  <div className="absolute top-full -right-8 sm:right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          onSwitchLanguage(lang.code);
                          setIsLangDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                          language === lang.code ? 'text-emerald-600 bg-emerald-50' : 'text-gray-700'
                        }`}
                      >
                        <span className="mr-2">{lang.flag}</span>
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* User Authentication */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-emerald-600 transition-colors border border-gray-200 rounded-lg hover:border-emerald-300"
                >
                  <User className="w-4 h-4" />
                  <span>{user.name || user.email}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {isUserDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                      <a
                        href="/trading"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        {t.platform}
                      </a>
                      <hr className="my-2" />
                      <button
                        onClick={() => {
                          onLogout();
                          setIsUserDropdownOpen(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 inline mr-2" />
                        {t.logout}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={onLogin}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors border border-gray-200 rounded-lg hover:border-emerald-300"
                >
                  {t.login}
                </button>
                <button
                  onClick={onRegister}
                  className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {t.register}
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button - Visible only on mobile */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-emerald-600 transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 bg-white">
            <nav className="flex flex-col space-y-4">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    setIsMenuOpen(false);
                  }}
                  className={`text-left px-4 py-2 text-sm font-medium transition-colors hover:text-emerald-600 ${
                    activeSection === item.id ? 'text-emerald-600' : 'text-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              <hr className="border-gray-200" />
              
              {/* Language in mobile */}
              <div className="px-4">
                <div className="text-sm font-medium text-gray-700 mb-2">{tt('Язык / Language')}</div>
                <div className="flex space-x-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        onSwitchLanguage(lang.code);
                        setIsMenuOpen(false);
                      }}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        language === lang.code
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-600'
                          : 'border-gray-300 text-gray-700 hover:border-emerald-300'
                      }`}
                    >
                      <span className="mr-1">{lang.flag}</span>
                      {lang.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              
              {user ? (
                <>
                  <a
                    href="/trading"
                    className="px-4 py-2 text-sm text-gray-700 hover:text-emerald-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t.platform}
                  </a>
                  <button
                    onClick={() => {
                      onLogout();
                      setIsMenuOpen(false);
                    }}
                    className="text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                  >
                    {t.logout}
                  </button>
                </>
              ) : (
                <div className="px-4 space-y-2">
                  <button
                    onClick={() => {
                      onLogin();
                      setIsMenuOpen(false);
                    }}
                    className="w-full py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.login}
                  </button>
                  <button
                    onClick={() => {
                      onRegister();
                      setIsMenuOpen(false);
                    }}
                    className="w-full py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200"
                  >
                    {t.register}
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;